import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button } from '@mui/material'

import { destroyShareAsync, selectShareToMeForPage } from './sharesSlice'
import { fetchPagesAsync } from '../pages/pagesSlice'
import { setFlashAlert, setFlashNotice } from '../ui/uiSlice'
import { showFailedRequest } from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'

// "Remove", on a page someone else shared with me, so I am not stuck with a page
// because I accepted it once (0.6.27.10, item 5).
//
// This removes the share, not the page: the page is the owner's and stays theirs.
// The same endpoint serves the owner's unshare, since removing is the one change
// either side of a share may make.
//
// Nothing here appears on my own pages, which have `DeletePage` instead, and
// nothing appears if the store has no share for the page — the pages list and the
// shares list are fetched separately, so a page can be in hand before its share
// is.
// `color: inherit` and not the default primary: the entry this sits in is white
// normally but the theme's `info.light` with white text when it is the page you
// are on, and a primary-blue label on that blue was barely readable. Taking the
// entry's own text colour works on both, and the underline is what says it is
// something to press.
const classes = {
  button: {
    marginTop: 0.5,
    paddingLeft: 0,
    paddingRight: 0,
    minWidth: 0,
    color: 'inherit',
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}

export default function RemoveSharedPage({ pageId, currentPageId }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const share = useSelector(selectShareToMeForPage(pageId))
  const [removing, submit] = useSubmitting()

  if (!share) {
    return null
  }

  const handleClick = (e) => {
    // The whole entry is a link to the page, so the click must not reach it: the
    // page is about to stop being mine to open.
    e.preventDefault()
    e.stopPropagation()

    submit(async () => {
      const result = await dispatch(destroyShareAsync(share.id))

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }

      if (result.response.status !== 204) {
        dispatch(setFlashAlert('Could not remove that page from your list.'))
        return
      }

      // The page has to leave the groups in the drawer, which are built from the
      // pages and not the shares.
      await dispatch(fetchPagesAsync())

      // Said in full, because removing someone else's page is easy to misread as
      // deleting it. The flash and any navigation go in the same run, with no
      // `await` between: `clearFlashOnNavigation` keeps a message set by whoever is
      // navigating and clears anything older.
      // Double quotes: the possessive apostrophe can't live in a single-quoted
      // string, which is what the rest of this file uses.
      const owner = share.owner_login ? ` ${share.owner_login}'s` : " the owner's"
      dispatch(
        setFlashNotice(
          `Removed "${share.page_title}" from your pages. ` +
            `The page itself is still${owner}, and they would have to share it again.`
        )
      )

      // Standing on a page that is no longer yours to read would leave a screen
      // whose next request is refused, so leave it first.
      if (pageId === currentPageId) {
        navigate('/app/pages')
      }
    })
  }

  return (
    <Button
      size='small'
      sx={classes.button}
      loading={removing}
      onClick={handleClick}
    >
      Remove from my pages
    </Button>
  )
}
